import { ChangeDetectionStrategy, Component, HostListener, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, of } from 'rxjs';

import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { map, take } from 'rxjs/operators';
import { BaseContainer } from '../../core/common/containers/base.abstract.container';
import { ExportProfile } from '../../core/profiles/entities/profile.entity';
import { DialogConfig } from '../../dynamic-dialog/entities/dialog-config.entity';
import { DialogRef } from '../../dynamic-dialog/entities/dialog-ref.class';
import { ActionItem } from '../../elements/components/action-item/entities/action-item.entity';
import { ShFormMultiSelectModule } from '../../form-controls/components/form-multi-select/form-multi-select.module';
import { MultiFormSelectOptions } from '../../form-controls/entities/multi-form-select-options.entity';
import { multiFormSelectOptionWrapper } from '../../form-controls/helpers/multi-form-select-options-wrapper-helper';

export type CsvDialogResult = { type: 'submit'; profileId: string } | { type: 'cancel' };

@Component({
    selector: 'sh-csv-export-dialog',
    templateUrl: './csv-export-dialog.component.html',
    styleUrls: ['./csv-export-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, TranslateModule, ShFormMultiSelectModule]
})
export class ShCsvExportDialog extends BaseContainer implements OnInit {
    public dialogActionItems$: Observable<ActionItem[]>;

    public profileOptions$: Observable<MultiFormSelectOptions>;

    public baseForm: UntypedFormGroup;
    public isFormValid$: Observable<boolean>;

    constructor(
        public config: DialogConfig,
        private readonly dialog: DialogRef<CsvDialogResult>,
        private readonly formBuilder: UntypedFormBuilder
    ) {
        super();
    }

    @HostListener('document:keydown.enter', ['$event'])
    handleEnter(event: { target: HTMLInputElement }) {
        // Prevent submitting when form is invalid
        if (!this.baseForm.valid) {
            return;
        }
        this.prepare();
    }

    ngOnInit(): void {
        this.initForm();

        this.initProfileStream();
    }

    private initForm(): void {
        this.baseForm = this.formBuilder.group({
            profile: [null, Validators.required]
        });
    }

    private initProfileStream(): void {
        const exportProfiles$: Observable<ExportProfile[]> = this.config.data.exportProfiles$ ?? of([]);

        this.profileOptions$ = exportProfiles$.pipe(
            map(exportProfiles => exportProfiles.map(profile => ({ label: profile.Name, value: profile.Id }))),
            map(options => multiFormSelectOptionWrapper(options)),
            take(1)
        );
    }

    public prepare(): void {
        this.closeDialog();
    }

    private closeDialog(): void {
        // due to multi select so `profile.value` is array. Hence hardcode to first item in array
        const startCSVBatchWithProfileSelected = this.baseForm.controls.profile.value[0];
        this.dialog.close({ type: 'submit', profileId: startCSVBatchWithProfileSelected });
    }

    public cancel(): void {
        const output: CsvDialogResult = { type: 'cancel' };
        this.dialog.close(output);
    }
}
